import React, { useMemo, useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useAggregatedLogs } from "../hooks/useAggregatedLogs";
import { useFilteredLogs } from "../hooks/useFilteredLogs";

function AnalysisModal({ token, onClose }) {
  const { aggregatedLogs, loading, error } = useAggregatedLogs(token);

  const [filters, setFilters] = useState({
    topicNames: [],
    dateRange: { start: "", end: "" },
    severities: [],
  });

  const [search, setSearch] = useState({ keyword: "", isRegex: false });
  const [sortCriteria, setSortCriteria] = useState("timestamp");

  const filteredLogs = useFilteredLogs(
    aggregatedLogs,
    filters,
    search,
    sortCriteria
  );

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const allTopics = [...new Set(aggregatedLogs.map((log) => log.topic))];

  const handleTopicCheckbox = (topic) => {
    setFilters((prevFilters) => {
      const isSelected = prevFilters.topicNames.includes(topic);
      return {
        ...prevFilters,
        topicNames: isSelected
          ? prevFilters.topicNames.filter((name) => name !== topic)
          : [...prevFilters.topicNames, topic],
      };
    });
  };

  const highlightedLogs = useMemo(() => {
    if (!search.keyword) return filteredLogs;
    const regex = new RegExp(`(${search.keyword})`, "gi");
    return filteredLogs.map((log) => ({
      ...log,
      highlightedContent: log.content.replace(regex, "<mark>$1</mark>"),
    }));
  }, [filteredLogs, search.keyword]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={handleOverlayClick}
    >
      <div className="bg-white rounded-xl shadow-lg w-11/12 h-[90%] p-3 overflow-y-auto flex flex-col">
        {/* Modal Header with Title and Close button */}
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-bold">Log Analysis</h2>
          <button onClick={onClose}>
            <FontAwesomeIcon
              icon={faXmark}
              className="text-gray-500 hover:text-black text-2xl align-middle"
            />
          </button>
        </div>

        {/* Search Bar (Sticky) */}
        <div className="sticky top-0 bg-white z-10 mb-4">
          <input
            type="text"
            placeholder="Search logs..."
            value={search.keyword}
            onChange={(e) => setSearch({ ...search, keyword: e.target.value })}
            className="w-full border rounded px-2 py-1 mb-2"
          />
          {/* Date and Time range filters */}
          <div className="flex space-x-2">
            <input
              type="datetime-local"
              value={filters.dateRange.start}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  dateRange: { ...filters.dateRange, start: e.target.value },
                })
              }
              className="border rounded px-2 py-1 flex-1"
            />
            <input
              type="datetime-local"
              value={filters.dateRange.end}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  dateRange: { ...filters.dateRange, end: e.target.value },
                })
              }
              className="border rounded px-2 py-1 flex-1"
            />
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left sidebar for checkboxes */}
          <div className="w-1/4 border-r pr-4 overflow-y-auto">
            <h3 className="font-semibold mb-2">Filter by Topics</h3>
            <div className="space-y-1">
              {allTopics.map((topic) => (
                <label key={topic} className="block cursor-pointer">
                  <input
                    type="checkbox"
                    className="mr-2"
                    checked={filters.topicNames.includes(topic)}
                    onChange={() => handleTopicCheckbox(topic)}
                  />
                  {topic}
                </label>
              ))}
            </div>
          </div>

          {/* Right side for logs */}
          <div className="w-3/4 pl-4 overflow-y-auto">
            {loading && <div>Loading logs...</div>}
            {error && <div className="text-red-500">{error}</div>}

            <div className="space-y-2">
              {highlightedLogs.map((log) => (
                <div key={log.id} className="p-2 border-b">
                  <div className="text-sm font-semibold text-delft-blue">
                    {log.topic}
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(log.timestamp).toLocaleString()}
                  </div>
                  <pre
                    className="text-xs text-gray-800 mt-2 whitespace-pre-wrap"
                    dangerouslySetInnerHTML={{
                      __html: log.highlightedContent || log.content,
                    }}
                  ></pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalysisModal;
